import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getBookAddresses, updateBookAddresses } from '@/store/books/actions';
import { createStudyPlan } from '@/store/study-plan/actions';
import { createUserTopic, deleteUserTopic } from '@/store/user-topics/actions';

import { selectOpenDate } from '@/store/course/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Button, message, Tooltip } from 'antd';
import CalendarModal from '@/pages/CalendarModal';
import { CheckOutlined, HeartFilled, HeartOutlined, PlusOutlined, StopFilled, SyncOutlined } from '@ant-design/icons';

import AddressBook from '../AddressBook/AddressBook';
import RepeatDisclaimer from '../RepeatDisclaimer';
import { disabledStatus, notAddStatus } from './constants';

import { checkActiveCourse } from '@shared/utils/course-utils';
import {
  getWithoutSchedule,
  isConference,
  isCorpCourseWithOrWithoutApprove,
  isEvent,
  isExternalCourse,
} from '@shared/utils/topic-utils';

import Utils from '@/Utils';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

import css from './MaterialButton.module.scss';

import { SCHEDULED } from '@/constants/study-plan-statuses';
import { ADD_REPEAT, NOT_ADD } from '@/constants/studyPlanStatus';

const MaterialButtonComp = ({ mini = false, miniIcon, children, className, ...rest }) => {
  switch (mini) {
    case true:
      return (
        <Tooltip title={children} placement='bottomRight'>
          <Button
            className={classNames(className, css['MaterialButton-mini'])}
            shape='circle'
            icon={miniIcon}
            {...rest}
            size='small'
          />
        </Tooltip>
      );

    default:
      return (
        <Button className={className} {...rest}>
          {children}
        </Button>
      );
  }
};

const MaterialButton = ({
  topic,
  inline,
  mini,
  className,
  buttonClassName,
  noFavorite = false,
  onSuccess,
  planType,
  flagTopicId,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Стейты модальных окон
  const [calendarModal, setCalendarModal] = useState({
    visible: false,
    planningTopicId: null,
    planningTopicType: {},
    topicInfo: null,
    startDate: null,
  });
  const [addressState, setAdressState] = useState({
    isAddressBookVisible: false,
    calendarModalInfo: {},
    addresses: [],
  });

  const [addressId, setAddressId] = useState(0);

  // Контроль состояния кнопок
  const [isFavourite, setIsFavourite] = useState(topic?.isFavourite || topic?.favourite);
  const [studyPlan, setStudyPlan] = useState(topic?.planStatus?.planStatus || null);
  const [userPlanType, setUserPlanType] = useState(planType || topic?.userPlanType);

  const { currentUser } = useSelector(selectUsers);
  const openDate = useSelector(selectOpenDate);

  const checkCoursePlan = topic?.type && !studyPlan;

  // Функция добавить в избранное
  const handleChangeFavourite = debounce(() => {
    if (isFavourite) {
      dispatch(deleteUserTopic(topic?.id, true, true, () => setIsFavourite(false)));
    } else {
      dispatch(createUserTopic(currentUser.id, topic?.id, () => setIsFavourite(true)));
    }
  }, 300);

  const toMyCollectionButton = !noFavorite && (
    <Button
      type='link'
      onClick={handleChangeFavourite}
      icon={
        isFavourite ? (
          <HeartFilled data-qa='removeFromFavoritesButton' className={css['MaterialButton-heart']} />
        ) : (
          <HeartOutlined data-qa='addToFavoritesButton' className={css['MaterialButton-heart']} />
        )
      }
      size='large'
      disabled={!Utils.isOnline()}
    ></Button>
  );

  const showCalendarModal = (topicId, type, startDate, topicInfo) => {
    setCalendarModal({
      visible: true,
      planningTopicId: topicId,
      planningTopicType: type,
      topicInfo,
      startDate,
    });
  };

  const closeCalendarModal = () => {
    setCalendarModal({
      visible: false,
      planningTopicId: null,
      planningTopicType: {},
      topicInfo: null,
      startDate: null,
    });
  };

  const handleStudyPlanCalendar = (
    topicId,
    planningMonths = [],
    comment,
    withoutSchedule,
    rest,
    planInfo,
    selectedEventId
  ) => {
    for (let i = 0; i < planningMonths.length; i++) {
      dispatch(
        createStudyPlan(
          currentUser.id,
          topicId,
          planningMonths[i],
          SCHEDULED,
          comment,
          () => {
            if (addressId > 0) {
              dispatch(updateBookAddresses(addressId, currentUser.id));
              setAddressId(0);
            }

            // ДОП ФУНКЦИЯ ЕСЛИ НУЖНО ЧТО-ТО ОБНОВИТЬ ПОСЛЕ ДОБАВЛЕНИЯ В ПЛАН
            onSuccess?.();
            setStudyPlan(SCHEDULED);
            setUserPlanType(NOT_ADD);

            closeCalendarModal();
          },
          false,
          getWithoutSchedule(calendarModal?.planningTopicType?.id, withoutSchedule),
          '',
          selectedEventId
        )
      );
    }
  };

  const showAddressBookModal = (topicId, type, startDate, topicInfo) => {
    dispatch(
      getBookAddresses(
        topicId,
        data => {
          const addresses = data.map(item => ({
            id: item.id,
            address: item.addressDto.address,
            accessible: item.accessible,
          }));

          if (addresses.length > 0) {
            setAdressState({
              addresses,
              isAddressBookVisible: true,
              calendarModalInfo: { topicId, type, startDate, topicInfo },
            });
            return;
          }

          showCalendarModal(topicId, type, startDate, topicInfo);
          setAdressState({ ...addressState, calendarModalInfo: {} });
        },
        () => setAdressState({ ...addressState, addresses: [] })
      )
    );
  };

  const handleStudyPlanClick = async () => {
    const disclaimerResult = retakeTopic && (await RepeatDisclaimer.Modal());

    if (disclaimerResult || !retakeTopic) {
      if (topic?.isTopicFull) {
        message.warning(t('materialCard:topicFull'));
        return;
      }

      const executeFunc = topic?.qiwiLibrary ? showAddressBookModal : showCalendarModal;

      const topicDate = () => {
        if (isConference(topic?.type?.id) && flagTopicId && Array.isArray(topic?.dates)) {
          return topic?.dates;
        } else if (Array.isArray(topic?.datesCourseEvent)) {
          return topic?.datesCourseEvent;
        }
      };

      executeFunc(topic?.id, topic?.type, topic?.startDate, {
        id: topic?.id,
        independent: topic?.independent,
        startDate: topic?.startDate,
        topicCost: topic?.cost,
        topicDates: topicDate(),
        link: topic?.firstLink || topic?.link || (topic?.links && topic?.links?.[0]?.url),
        eventCount: topic?.futureEvents,
        topicComment: topic?.comment,
        type: topic.type,
      });
    }
  };

  const handleAddressBookSubmit = currentAddressId => {
    const { topicId, type, startDate, topicInfo } = addressState?.calendarModalInfo;

    setAdressState({ isAddressBookVisible: false, calendarModalInfo: {}, addresses: [] });
    setAddressId(currentAddressId > 0 ? currentAddressId : 0);

    showCalendarModal(topicId, type, startDate, topicInfo);
  };

  const correctDate =
    isEvent(topic?.type?.id) || isConference(topic?.type?.id)
      ? dayjs(`${topic?.endDate + ' ' + topic?.endTime}`, 'YYYY-MM-DD HH:mm').format()
      : '';

  const checkIsEnd =
    (isEvent(topic?.type?.id) || isConference(topic?.type?.id)) &&
    topic?.planStatus &&
    !topic?.planStatus.planStatus &&
    dayjs(correctDate).isBefore(dayjs());

  const retakeTopic = userPlanType === ADD_REPEAT;

  const disabledSelect = calendarModal?.planningTopicType?.id === 8;

  return (
    <>
      {(topic?.status === disabledStatus && checkCoursePlan) ||
      checkIsEnd ||
      (!studyPlan && !checkActiveCourse({ topic, openDate: openDate?.openDate }, 'isTemplate')) ? (
        <div
          className={classNames(
            css['MaterialButton-group'],
            {
              [css['MaterialButton-inline']]: inline,
            },
            className
          )}
        >
          <MaterialButtonComp
            className={classNames(css['MaterialButton'], buttonClassName)}
            type='primary'
            disabled
            size='large'
            mini={mini}
            miniIcon={<StopFilled />}
          >
            <div className={css['MaterialCard-noActive']} />
            {t('materialCard:unActive')}
          </MaterialButtonComp>
          {toMyCollectionButton}
        </div>
      ) : studyPlan ? (
        <div
          className={classNames(
            css['MaterialButton-group'],
            {
              [css['MaterialButton-inline']]: inline,
            },
            className
          )}
        >
          <MaterialButtonComp
            className={classNames(css['MaterialButton'], buttonClassName)}
            onClick={retakeTopic && handleStudyPlanClick}
            size='large'
            type={retakeTopic && 'primary'}
            ghost={!retakeTopic}
            mini={mini}
            miniIcon={retakeTopic ? <SyncOutlined /> : <CheckOutlined />}
          >
            {t(`materialCard:${Utils.checkTitleAddToPlan({ userPlanType })}`)}
          </MaterialButtonComp>
          {toMyCollectionButton}
        </div>
      ) : (
        <div
          className={classNames(
            css['MaterialButton-group'],
            {
              [css['MaterialButton-inline']]: inline,
            },
            className
          )}
        >
          <MaterialButtonComp
            id='addToPlan'
            className={classNames(css['MaterialButton'], buttonClassName)}
            onClick={handleStudyPlanClick}
            type={topic?.status === disabledStatus ? 'default' : 'primary'}
            disabled={topic?.status === disabledStatus || (!Utils.isOnline() && topic?.userPlanType === notAddStatus)}
            size='large'
            mini={mini}
            miniIcon={<PlusOutlined />}
            data-qa='addToPlanBtn'
          >
            {t('materialCard:addToPlan')}
          </MaterialButtonComp>
          {toMyCollectionButton}
        </div>
      )}

      {calendarModal.visible && (
        <CalendarModal
          topicInfo={calendarModal.topicInfo}
          comment={calendarModal?.topicInfo?.topicComment}
          eventTypeId={calendarModal?.planningTopicType?.id}
          disabledSelect={disabledSelect}
          dateEvent={dayjs(calendarModal.startDate).format('YYYY-MM-DD')}
          title={!disabledSelect ? t('collectionMaterials:selectPlan') : t('collectionMaterials:scheduleEvent')}
          open={calendarModal.visible}
          onOk={handleStudyPlanCalendar}
          onCancel={closeCalendarModal}
          dates={calendarModal?.topicInfo?.topicDates}
          okText={t('collectionMaterials:add')}
          {...(isExternalCourse(calendarModal?.planningTopicType?.id) && {
            okText: t('collectionMaterials:addToPlan'),
            headerFixToModal: true,
          })}
          {...(isEvent(calendarModal?.planningTopicType?.id) && {
            okText: t('collectionMaterials:addToPlan'),
            headerFixToModal: true,
          })}
          planningTopicId={calendarModal.planningTopicId}
          hasEvents={calendarModal?.topicInfo?.eventCount !== 0}
          selectMonthEvents={
            isCorpCourseWithOrWithoutApprove(calendarModal?.planningTopicType?.id) ||
            isExternalCourse(calendarModal?.planningTopicType?.id)
          }
          currentUser={currentUser}
        />
      )}
      {addressState.isAddressBookVisible && (
        <AddressBook
          textWindowTitle={t('collectionMaterials:addressBookWindowTitle')}
          textSubmitButton={t('collectionMaterials:addressBookSubmitButton')}
          textAccessible={t('collectionMaterials:addressBookaccessible')}
          textNotAccessible={t('collectionMaterials:addressBookNotAccessible')}
          textReadOnline={t('collectionMaterials:addressBookReadOnline')}
          onButtonClose={() => setAdressState({ isAddressBookVisible: false, calendarModalInfo: {}, addresses: [] })}
          onButtonSubmit={handleAddressBookSubmit}
          open={addressState.isAddressBookVisible}
          data={addressState?.addresses}
        />
      )}
      <RepeatDisclaimer />
    </>
  );
};

export default MaterialButton;
